import { useState, useRef, useEffect } from 'react'
import { ShieldCheckIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'

export default function MfaSetup({ onComplete, inline = false }) {
  const { getAccessToken, completeMfaSetup, logout } = useAuth()
  const [step, setStep] = useState(1) // 1: QR, 2: Verify, 3: Backup codes
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [setupLoading, setSetupLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    startSetup()
  }, [])

  useEffect(() => {
    if (step === 2) inputRef.current?.focus()
  }, [step])

  const startSetup = async () => {
    try {
      const token = getAccessToken()
      const res = await fetch('/api/auth/mfa/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      const data = await res.json()
      if (data.success) {
        setQrCode(data.qrCodeDataUrl)
        setSecret(data.secret)
      } else {
        setError(data.error || 'Failed to start MFA setup')
      }
    } catch (err) {
      setError('Failed to start MFA setup')
    } finally {
      setSetupLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e?.preventDefault()
    setError('')
    setLoading(true)

    try {
      const token = getAccessToken()
      const res = await fetch('/api/auth/mfa/setup/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ token: code })
      })
      const data = await res.json()
      if (data.success) {
        setBackupCodes(data.backupCodes)
        setStep(3)
      } else {
        setError(data.error || 'Invalid code')
        setCode('')
        inputRef.current?.focus()
      }
    } catch (err) {
      setError('Verification failed')
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '')
    setCode(val)
    if (val.length === 6) {
      setTimeout(() => handleVerifyDirect(val), 0)
    }
  }

  const handleVerifyDirect = async (directCode) => {
    setError('')
    setLoading(true)
    try {
      const token = getAccessToken()
      const res = await fetch('/api/auth/mfa/setup/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ token: directCode })
      })
      const data = await res.json()
      if (data.success) {
        setBackupCodes(data.backupCodes)
        setStep(3)
      } else {
        setError(data.error || 'Invalid code')
        setCode('')
        inputRef.current?.focus()
      }
    } catch (err) {
      setError('Verification failed')
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleComplete = () => {
    if (onComplete) {
      onComplete()
    } else {
      completeMfaSetup()
    }
  }

  if (setupLoading) {
    return (
      <div className={inline ? 'flex items-center justify-center py-8' : 'min-h-screen bg-slate-900 flex items-center justify-center'}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-500" />
      </div>
    )
  }

  const content = (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-500/10 rounded-xl mb-3">
          <ShieldCheckIcon className="h-6 w-6 text-amber-500" />
        </div>
        <h2 className="text-xl font-semibold text-white">Set Up Two-Factor Authentication</h2>
        {!inline && (
          <p className="text-sm text-slate-400 mt-1">Your department requires MFA for all users</p>
        )}
      </div>

      {/* Step indicators */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              s < step ? 'bg-amber-500 text-slate-900'
              : s === step ? 'bg-amber-500/20 text-amber-500 ring-2 ring-amber-500'
              : 'bg-slate-700 text-slate-500'
            }`}>
              {s < step ? <CheckIcon className="h-4 w-4" /> : s}
            </div>
            {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-amber-500' : 'bg-slate-700'}`} />}
          </div>
        ))}
      </div>

      <div className={inline ? '' : 'card'}>
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: QR Code */}
        {step === 1 && (
          <div>
            <p className="text-sm text-slate-300 mb-4">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
            </p>

            <div className="flex justify-center mb-4">
              {qrCode && (
                <img src={qrCode} alt="TOTP QR Code" className="w-48 h-48 rounded-lg bg-white p-2" />
              )}
            </div>

            <div className="mb-4">
              <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this code manually:</p>
              <div className="bg-slate-800 rounded-lg px-3 py-2 font-mono text-sm text-amber-400 break-all select-all">
                {secret}
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full btn btn-primary py-2.5"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 2 && (
          <form onSubmit={handleVerify}>
            <p className="text-sm text-slate-300 mb-4">
              Enter the 6-digit code from your authenticator app to verify setup.
            </p>

            <div className="mb-6">
              <input
                ref={inputRef}
                type="text"
                value={code}
                onChange={handleCodeChange}
                maxLength={6}
                placeholder="000000"
                autoComplete="one-time-code"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center text-xl font-mono tracking-widest placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setCode(''); setError('') }}
                className="px-4 py-2.5 text-slate-400 hover:text-white text-sm transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 btn btn-primary py-2.5 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Backup Codes */}
        {step === 3 && (
          <div>
            <p className="text-sm text-slate-300 mb-2">
              Save these backup codes in a safe place. Each code can only be used once.
            </p>
            <p className="text-xs text-amber-400 mb-4">
              You won't be able to see these codes again.
            </p>

            <div className="bg-slate-800 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((c, i) => (
                  <div key={i} className="font-mono text-sm text-slate-200 text-center py-1">
                    {c}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={copyBackupCodes}
              className="w-full mb-4 py-2 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm transition-colors"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4 text-green-400" />
                  <span className="text-green-400">Copied!</span>
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy All Codes
                </>
              )}
            </button>

            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-slate-300">
                I have saved my backup codes in a secure location
              </span>
            </label>

            <button
              onClick={handleComplete}
              disabled={!confirmed}
              className="w-full btn btn-primary py-2.5 disabled:opacity-50"
            >
              Complete Setup
            </button>
          </div>
        )}
      </div>

      {!inline && (
        <div className="mt-4 text-center">
          <button onClick={logout} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">
            Sign out
          </button>
        </div>
      )}
    </>
  )

  if (inline) {
    return content
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center safe-screen">
      <div className="w-full max-w-md">
        {content}
      </div>
    </div>
  )
}
